import PageHeader from "../../components/ui/PageHeader";
import { Box, Button, FormControl, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, Typography, useMediaQuery } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import { Figure, getStats, SiteStats, Stats as StatsData } from "../../service/StatsService";
import { outlinedButton, StyledTableCell, StyledTableRow, TableWrapper } from "../logs/logStyles";

// Hours: last hour, 4 hours, day, 7, 30 and 90 days
const PERIODS = [1, 4, 24, 168, 720, 2160];
const TOP_TYPES = ["url", "browser", "device", "country"] as const;

const isSupport = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}").adminRole === "support";
  } catch {
    return false;
  }
};

const duration = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

/** Change against the previous period of the same length */
const change = (figure: Figure) => {
  if (!figure.prev) return "-";
  const percent = Math.round(((figure.value - figure.prev) / figure.prev) * 100);
  return `${percent > 0 ? "+" : ""}${percent} %`;
};

/** One app (tenant portal or player app): key figures, per day and top lists - tables, like the log overview */
function SiteSection({ title, site, perHour }: { title: string; site: SiteStats; perHour: boolean }) {
  const [t] = useTranslation("global");
  const { figures } = site;
  const rows: [string, string | number, string][] = [
    [t("stats.visitors"), figures.visitors.value, change(figures.visitors)],
    [t("stats.visits"), figures.visits.value, change(figures.visits)],
    [t("stats.pageviews"), figures.pageviews.value, change(figures.pageviews)],
    [t("stats.bounceRate"), `${figures.bounceRate.value} %`, change(figures.bounceRate)],
    [t("stats.avgVisit"), duration(figures.avgVisitSeconds.value), change(figures.avgVisitSeconds)],
  ];

  return (
    <Box sx={{ marginBottom: "2%" }}>
      <Box sx={{ width: "100%", mt: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <Typography variant="h6">{title}</Typography>
        <Button variant="outlined" href={site.dashboardUrl} target="_blank" rel="noopener noreferrer" sx={outlinedButton}>
          {t("stats.openUmami")}
        </Button>
      </Box>

      <TableWrapper component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t("stats.keyFigures")}</StyledTableCell>
              <StyledTableCell align="right">{t("stats.value")}</StyledTableCell>
              <StyledTableCell align="right">{t("stats.change")}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(([label, value, diff]) => (
              <StyledTableRow key={label}>
                <TableCell>{label}</TableCell>
                <TableCell align="right">{value}</TableCell>
                <TableCell align="right">{diff}</TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableWrapper>

      <Box sx={{ width: "100%", display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, columnGap: 2 }}>
        {TOP_TYPES.map((type) => (
          <TableWrapper key={type} component={Paper} sx={{ width: "100%" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <StyledTableCell>{t(`stats.top.${type}`)}</StyledTableCell>
                  <StyledTableCell align="right">{t("stats.visitors")}</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {site.top[type].map((row) => (
                  <StyledTableRow key={row.name}>
                    <TableCell sx={{ wordBreak: "break-all" }}>{row.name}</TableCell>
                    <TableCell align="right">{row.count}</TableCell>
                  </StyledTableRow>
                ))}
                {!site.top[type].length && (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ textAlign: "center" }}>
                      {t("stats.none")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableWrapper>
        ))}
      </Box>

      <TableWrapper component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t(perHour ? "stats.hour" : "stats.day")}</StyledTableCell>
              <StyledTableCell align="right">{t("stats.visitors")}</StyledTableCell>
              <StyledTableCell align="right">{t("stats.pageviews")}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {site.days.map((row) => (
              <StyledTableRow key={row.day}>
                <TableCell>
                  {perHour
                    ? `${new Date(row.day.replace(" ", "T")).toLocaleDateString("da-DK")} kl. ${row.day.slice(11, 16)}`
                    : new Date(row.day).toLocaleDateString("da-DK")}
                </TableCell>
                <TableCell align="right">{row.visitors}</TableCell>
                <TableCell align="right">{row.pageviews}</TableCell>
              </StyledTableRow>
            ))}
            {!site.days.length && (
              <TableRow>
                <TableCell colSpan={3} sx={{ textAlign: "center" }}>
                  {t("stats.none")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>
    </Box>
  );
}

/** admin → Statistik: visitors of the tenant portal and the player app, from Umami (no personal data) */
export default function Stats() {
  const [t] = useTranslation("global");
  const isMobile = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate();
  const [hours, setHours] = useState(720);
  const [data, setData] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getStats(hours, isMobile, navigate).then((result) => {
      setData(result);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours]);

  return (
    <Layout>
      <PageHeader title={t("stats.title")} description={t("stats.intro")} />
      <Box sx={{ display: "flex", mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t("stats.period")}</InputLabel>
          <Select label={t("stats.period")} value={hours} onChange={(event) => setHours(Number(event.target.value))}>
            {PERIODS.map((period) => (
              <MenuItem key={period} value={period}>
                {t(`stats.hours${period}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading && (
        <Typography sx={{ textAlign: "center" }} color="text.secondary">
          {t("stats.loading")}
        </Typography>
      )}
      {!loading && data && !data.configured && (
        <Typography sx={{ width: "100%", textAlign: "center" }}>
          {t("stats.notConfigured")}{" "}
          {!isSupport() && <Link to="/platform-settings">{t("stats.toSettings")}</Link>}
        </Typography>
      )}
      {!loading && data?.configured && data.ok === false && (
        <Typography sx={{ width: "100%", textAlign: "center" }} color="error.main">
          {t("stats.unavailable")} ({data.message})
        </Typography>
      )}
      {!loading && data?.ok && (
        <>
          {data.sites?.tenant && <SiteSection title={t("stats.tenant")} site={data.sites.tenant} perHour={data.unit === "hour"} />}
          {data.sites?.player && <SiteSection title={t("stats.player")} site={data.sites.player} perHour={data.unit === "hour"} />}
        </>
      )}
    </Layout>
  );
}
